import {
  Button,
  FormField,
  Input,
  HighlightIcon,
  Avatar,
  OptionGroup,
  AutocompleteNoOptions,
  type IconName,
  type HighlightIconVariant,
  PanelContent,
  PanelNavigation,
  PanelFooter,
  PanelBody,
  PanelArea,
  SwitchField,
  Link,
} from '@dev-spendesk/grapes';
import { type PayableRule } from '@spendesk/bookkeeping-core-types';
import { useState, useCallback, type Dispatch } from 'react';
import { Trans } from 'react-i18next';
import { useHistory } from 'react-router-dom'; // Import useParams

import { AutocompleteAsync } from 'common/components/AutocompleteAsync';
import {
  type Field,
  type SubfilterValue,
  type SubfilterValueWithId,
  useActiveFilter,
} from 'common/components/FilterBuilder';
import { useFilterBuilder } from 'common/components/FilterBuilder/FilterBuilderForm';
import { IconPicker } from 'common/components/IconPicker/IconPicker'; // Import IconPicker
import { QuerySuspense } from 'common/components/QuerySuspense';
import { fallbackSupplierLogoSrc } from 'common/components/SupplierLogo';
import {
  type TGlobalFunctionTyped,
  useTranslation,
} from 'common/hooks/useTranslation';
import { normalizeString } from 'common/utils/normalizeString';
import { useCompanyId } from 'modules/app/hooks/useCompanyId';
import { NotificationType, useNotifications } from 'modules/app/notifications';
import {
  useLoadMembersByIds,
  useSearchCustomFieldValues,
  useSearchExpenseAccounts,
  useSearchMembers,
  useSearchTaxAccounts,
} from 'modules/bookkeep/prepare-payables/components/PreparePayablesFiltersBar/components/PreparePayablesFiltersAdditionalFilters/hooks';
import {
  useCostCentersQuery,
  useCustomFieldsQuery,
} from 'modules/budgets/apis';
import { type CustomFieldDefinition } from 'modules/budgets/models/customFieldDefinition';
import { sortCostCenters } from 'modules/company/cost-centers/utils';
import { type EnrichedPayableRule } from 'modules/payable';
import {
  useGetFields,
  IdTypeWithPredefinedOptionsField,
  CustomFieldIdTypeField,
  getPayableTypeOptions,
  IdTypeField,
  AmountTypeField,
} from 'modules/payable/components';
import { VerticalLine } from 'modules/requests/components/common/VerticalLine';
import { SupplierFilterField } from 'modules/suppliers';
import { unwrapQuery } from 'src/core/api/unwrapQuery';
import { routeFor, routes } from 'src/core/constants/routes';
import { useRequestTypeEnablements } from 'src/core/modules/requests/hooks/useRequestTypeEnablements';

import { type FilterBuilderOutputAction, OutputsEditor } from './OutputsEditor';
import { PayableRuleConditionsEditor } from './PayableRuleConditionsEditor';
import './PayableRuleCreate.css';
import {
  type Output,
  useAutomaticallyFocusOnFirstEmptyConditionOrOutput,
} from './hooks/useAutomaticallyFocusOnFirstEmptyConditionOrOutput';
import {
  type Condition,
  filterValidConditions,
  validateConditions,
} from './utils/validateConditions';
import { filterValidOutputs, validateOutputs } from './utils/validateOutputs';
import {
  type CreatePayableRulePayload,
  useCreatePayableRule,
} from '../../hooks/useCreatePayableRule';
import { type MCRLimit } from '../../hooks/useGetPayableRulesMCRLimit';
import { useUpdatePayableRule } from '../../hooks/useUpdatePayableRule';

const staticConditionOptions = ['expenseAccount', 'vatAccount', 'costCenter'];

const staticOutputOptions = [
  'payableType',
  'supplier',
  'requestor',
  'grossAmount',
  'costCenter',
];

type Option = {
  key: string;
  label: string;
  thumbnailUrl?: string;
};

const autocompleteNoOptions = (
  t: TGlobalFunctionTyped,
  rawValue: string = '',
) => {
  return (
    <AutocompleteNoOptions>
      {rawValue === '' ? (
        t('misc.noResultFor', { search: rawValue })
      ) : (
        <div className="inline">
          <Trans
            i18nKey="misc.noResultFor"
            values={{ search: rawValue }}
            components={[<span key="noResultFor" />]}
          />
        </div>
      )}
    </AutocompleteNoOptions>
  );
};

export const PayableRuleCreateWrapper = ({
  mcrLimit,
}: {
  mcrLimit: MCRLimit;
}) => {
  const costCenterQueryState = useCostCentersQuery();
  return (
    <QuerySuspense
      queryState={costCenterQueryState}
      loading={null}
      fallback={() => null}
    >
      {(costCenters) => (
        <PanelContent DO_NOT_USE_isSidePanel>
          <PayableRuleCreate
            costCenters={sortCostCenters(costCenters)}
            mcrLimit={mcrLimit}
          />
        </PanelContent>
      )}
    </QuerySuspense>
  );
};

type Props = {
  costCenters: { id: string; name: string }[];
  payableRule?: EnrichedPayableRule;
  onBack?: () => void;
  mcrLimit: MCRLimit;
};

export const PayableRuleCreate = ({
  costCenters,
  onBack,
  payableRule,
  mcrLimit,
}: Props) => {
  const { dangerNotif, successNotif, pushNotif } = useNotifications();
  const { t } = useTranslation('global');
  const history = useHistory();
  const companyId = useCompanyId();
  const { enablements } = useRequestTypeEnablements();

  const fields = useGetFields({ variant: 'all-payables' });
  const optionFields = extractFields(fields, staticConditionOptions);
  const conditionFields = extractFields(fields, staticOutputOptions);

  const [ruleName, setRuleName] = useState(payableRule?.name ?? '');
  const [error, setError] = useState(false);
  const [icon, setIcon] = useState<IconName>('lightning');
  const [iconColor, setIconColor] = useState<HighlightIconVariant>('purple');
  const [conditions, setConditions] = useState<Condition[]>(
    payableRule?.rawConditions
      ? convertConditionsFromPayableRuleToForm(payableRule.rawConditions)
      : [],
  );
  const [outputs, setOutputs] = useState<Output[]>(
    payableRule?.rawOutputs
      ? convertOutputsToArray(payableRule.rawOutputs)
      : [],
  );
  const [isPayableRulePublished, setIsPayableRulePublished] = useState(
    payableRule?.state === 'published',
  );

  const validateRuleName = () => {
    const isValid = ruleName.trim() !== '';
    setError(!isValid);
    return isValid;
  };

  const activeFilter = useActiveFilter({
    filterIdUrlParameterName: 'payableRule',
    filterUrlParameterName: 'payableRule',
    savedFilters: [],
    fields,
  });

  const activeOutput = useActiveFilter({
    filterIdUrlParameterName: 'payableRule',
    filterUrlParameterName: 'payableRule',
    savedFilters: [],
    fields: optionFields,
  });

  const [createPayableRule] = useCreatePayableRule();
  const [updatePayableRule] = useUpdatePayableRule();

  const [, dispatchFilterAction] = useFilterBuilder(activeFilter);
  const [, dispatchOutputAction] = useFilterBuilder(activeOutput);

  const onSearchMembers = useSearchMembers();
  const onSearchMembersOptions = async (search: string) => {
    const members = await onSearchMembers(search);
    return members.map(({ id, fullName }) => ({ key: id, label: fullName }));
  };

  const onGetMembersByKeys = useLoadMembersByIds();
  const onGetMembersOptionsByKeys = async (ids: string[]) => {
    const members = await onGetMembersByKeys(ids);
    return members.map(({ id, fullname }) => ({ key: id, label: fullname }));
  };

  // Expense accounts
  const onSearchExpenseAccounts = useSearchExpenseAccounts(false);
  const onSearchExpenseAccountsOptions = async (search: string) => {
    const accounts = await onSearchExpenseAccounts(
      Array.isArray(search) ? search[0] : search,
    );
    return accounts.map(({ id, name }) => ({ key: id, label: name }));
  };
  const onLoadExpenseAccountsOptions = async () => {
    const accounts = await onSearchExpenseAccounts('');
    return accounts.map(({ id, name }) => ({ key: id, label: name }));
  };

  // Tax accounts
  const onSearchTaxAccounts = useSearchTaxAccounts();
  const onSearchTaxAccountsOptions = async (search: string) => {
    const accounts = await onSearchTaxAccounts(search);
    return accounts.map(({ id, name }) => ({ key: id, label: name }));
  };

  // Cost centers
  const onGetCostCentersOptionsByKeys = async (ids: string[]) => {
    return costCenters
      .filter(({ id }) => ids.includes(id))
      .map(({ id, name }) => ({ key: id, label: name }));
  };
  const onSearchCostCentersOptions = async (search: string) => {
    return costCenters
      .filter(({ name }) =>
        normalizeString(name).includes(normalizeString(search)),
      )
      .map(({ id, name }) => ({ key: id, label: name }));
  };

  const validateConditionsBeforeSave = () => {
    const validatedConditions = validateConditions(conditions, t);
    setConditions(validatedConditions);
    return validatedConditions.every(
      (condition) =>
        !condition.fieldError &&
        !condition.operatorError &&
        !condition.valueError,
    );
  };

  const validateOutputsBeforeSave = () => {
    const validatedOutputs = validateOutputs(outputs, t);
    setOutputs(validatedOutputs);
    return validatedOutputs.every(
      (output) => !output.fieldError && !output.valueError,
    );
  };

  const handleSave = useCallback(
    async (state: 'draft' | 'published') => {
      const isNameValid = validateRuleName();

      let validatedConditions = conditions;
      let validatedOutputs = outputs;

      if (state === 'published') {
        const isConditionsValid = validateConditionsBeforeSave();
        const isOutputsValid = validateOutputsBeforeSave();

        if (!isNameValid || !isConditionsValid || !isOutputsValid) {
          pushNotif({
            message: t(
              'bookkeep.integrations.settings.payableRules.validationError',
            ),
            type: NotificationType.Danger,
          });
          return;
        }
      } else {
        if (!isNameValid) {
          pushNotif({
            message: t(
              'bookkeep.integrations.settings.payableRules.validationError',
            ),
            type: NotificationType.Danger,
          });
          return;
        }
        // For draft, filter only valid conditions and outputs
        validatedConditions = filterValidConditions(conditions);
        validatedOutputs = filterValidOutputs(outputs);
      }

      try {
        const payload = transformPayload({
          icon,
          conditions: validatedConditions,
          outputs: validatedOutputs,
          state,
          name: ruleName,
        });
        if (!payableRule) {
          await createPayableRule(
            payload as unknown as CreatePayableRulePayload,
          );
          successNotif(
            t('bookkeep.integrations.settings.payableRules.created', {
              ruleName,
            }),
          );
        } else {
          const payloadWithEndpointParams = {
            ...payload,
            fromVersion: payableRule.version,
            endpointParams: {
              payableRuleId: payableRule.id,
            },
          };
          await updatePayableRule(payloadWithEndpointParams);
          successNotif(
            t('bookkeep.integrations.settings.payableRules.updated', {
              ruleName,
            }),
          );
        }
        history.push(
          routeFor(routes.COMPANY_ACCOUNTING_PAYABLE_RULES_SETUP.path, {
            company: companyId,
          }),
        );
      } catch (ruleCreatioError) {
        if (ruleCreatioError?.data?.reason === 'nameAlreadyExists') {
          dangerNotif(
            t(
              'bookkeep.integrations.settings.payableRules.nameAlreadyExistsError',
            ),
          );
        } else if (ruleCreatioError?.data?.reason === 'mcrLimitReached') {
          dangerNotif(
            <Trans
              i18nKey="bookkeep.integrations.settings.payableRules.mcrLimits.fullyReachedNotification"
              components={[
                <Link
                  key="intercom-message"
                  onClick={(event) => {
                    event.preventDefault();
                    window.Intercom('show');
                  }}
                  href=""
                >
                  -
                </Link>,
              ]}
            />,
          );
        } else {
          dangerNotif(t('bookkeep.integrations.settings.payableRules.error'));
        }
      }
    },
    [icon, conditions, ruleName, outputs, history, companyId],
  );

  const {
    conditionFieldName,
    conditionOperatorName,
    conditionValueName,
    outputFieldName,
    outputValueName,
  } = useAutomaticallyFocusOnFirstEmptyConditionOrOutput({
    conditions,
    outputs,
    disableOnMount: Boolean(payableRule),
  });

  return (
    <>
      <PanelBody className="panel-body">
        <PanelNavigation
          onClose={() => {
            history.push(
              routeFor(routes.COMPANY_ACCOUNTING_PAYABLE_RULES_SETUP.path, {
                company: companyId,
              }),
            );
          }}
        >
          {(titleId) => (
            <h2 className="heading-xl" id={titleId}>
              {payableRule
                ? t('bookkeep.integrations.settings.payableRules.details.title')
                : t('bookkeep.integrations.settings.payableRules.new')}
            </h2>
          )}
        </PanelNavigation>
        <PanelArea>
          <div>
            <div className="items-top payable-rule-input mb-12 flex">
              <IconPicker
                selectedIcon={icon}
                selectedColor={iconColor}
                onIconChange={(
                  newIcon: IconName,
                  newColor: HighlightIconVariant,
                ) => {
                  setIcon(newIcon);
                  setIconColor(newColor);
                }}
              />
              <FormField
                label="Rule Name"
                alertMessage={
                  error
                    ? t(
                        'bookkeep.integrations.settings.payableRules.errors.missingName',
                      )
                    : undefined
                }
                visuallyHideLabel
                className="w-full"
              >
                <Input
                  placeholder={t(
                    'bookkeep.integrations.settings.payableRules.rulenamePlaceholder',
                  )}
                  value={ruleName}
                  onChange={(event) => {
                    setRuleName(event.target.value);
                    setError(event.target.value.trim() === '');
                  }}
                  onBlur={() => validateRuleName()}
                />
              </FormField>
            </div>
            {payableRule ? (
              <div className="mb-12 flex items-center justify-between">
                <SwitchField
                  label={t(
                    'bookkeep.integrations.settings.payableRules.published',
                  )}
                  className="items-center"
                  isChecked={isPayableRulePublished}
                  onChange={async () => {
                    setIsPayableRulePublished(!isPayableRulePublished);
                  }}
                />
              </div>
            ) : null}
            <div className="separator mb-24 pt-12" />
            <div className="conditions-wrapper">
              <div className="mb-8 flex items-center gap-8">
                <HighlightIcon name="funnel" size={40} variant="blue" />
                <span className="title-l">
                  {t(
                    'bookkeep.integrations.settings.payableRules.conditionsTitle',
                  )}
                </span>
              </div>
              <div className="w-full">
                <PayableRuleConditionsEditor
                  fields={conditionFields}
                  conditions={conditions}
                  outputs={outputs}
                  mcrLimit={mcrLimit}
                  onConditionsChange={(updatedConditions) =>
                    setConditions(
                      updatedConditions as PayableRule['conditions'],
                    )
                  }
                  dispatchFilterAction={dispatchFilterAction}
                  renderSubfilterValue={({
                    subfilterValue,
                    updateSubfilterValue,
                    subfilterOperatorName,
                    subfilterValueName,
                  }) => {
                    const renderIdTypeWithPredefinedOptionsField = (
                      options: React.ComponentProps<
                        typeof IdTypeWithPredefinedOptionsField
                      >['options'],
                    ) => (
                      <IdTypeWithPredefinedOptionsField
                        options={options}
                        value={subfilterValue}
                        onChange={updateSubfilterValue}
                        subfilterOperatorName={subfilterOperatorName}
                        subfilterValueName={subfilterValueName}
                        operatorError={subfilterValue.operatorError}
                        valueError={subfilterValue.valueError}
                        dropdownClassName="customWidthForDropdown"
                        classNameOperatorWidth="w-[100px]"
                      />
                    );

                    const [, customFieldId] =
                      subfilterValue.field?.match(/customField-(.*)/) ?? [];
                    if (customFieldId) {
                      return (
                        <CustomFieldIdTypeField
                          customFieldId={customFieldId}
                          value={subfilterValue}
                          onChange={updateSubfilterValue}
                          subfilterOperatorName={subfilterOperatorName}
                          subfilterValueName={subfilterValueName}
                          operatorError={subfilterValue.operatorError}
                          valueError={subfilterValue.valueError}
                          dropdownClassName="customWidthForDropdown"
                          classNameOperatorWidth="w-[100px]"
                        />
                      );
                    }

                    switch (subfilterValue.field) {
                      case 'payableType': {
                        return renderIdTypeWithPredefinedOptionsField(
                          getPayableTypeOptions(
                            t,
                            enablements.isMileageAllowanceEnabled,
                            enablements.isPerDiemAllowanceEnabled,
                          ),
                        );
                      }

                      case 'requestor': {
                        return (
                          <IdTypeField
                            value={subfilterValue}
                            onChange={updateSubfilterValue}
                            subfilterOperatorName={subfilterOperatorName}
                            subfilterValueName={subfilterValueName}
                            onSearch={onSearchMembersOptions}
                            onGetByKeys={onGetMembersOptionsByKeys}
                            operatorError={subfilterValue.operatorError}
                            valueError={subfilterValue.valueError}
                            dropdownClassName="customWidthForDropdown"
                            classNameOperatorWidth="w-[100px]"
                            maxOptions={50}
                          />
                        );
                      }

                      case 'supplier': {
                        const getSupplierNamePrefix = (
                          option: Option | undefined,
                        ) => {
                          const defaultUrl = option?.label
                            ? `https://logo.clearbit.com/${option.label
                                .toLowerCase()
                                .replaceAll(' ', '')}.com`
                            : undefined;

                          return (
                            <Avatar
                              variant="square"
                              src={option?.thumbnailUrl ?? defaultUrl}
                              fallbackSrc={fallbackSupplierLogoSrc}
                              text={option?.label ?? ''}
                              size={24}
                            />
                          );
                        };

                        let selectedKeys = [];
                        if (typeof subfilterValue.value === 'string') {
                          selectedKeys = [subfilterValue.value];
                        } else if (Array.isArray(subfilterValue.value)) {
                          selectedKeys = subfilterValue.value;
                        }

                        return (
                          <IdTypeField<Option>
                            value={subfilterValue}
                            onChange={updateSubfilterValue}
                            subfilterOperatorName={subfilterOperatorName}
                            subfilterValueName={subfilterValueName}
                            renderPrefix={getSupplierNamePrefix}
                            operatorError={subfilterValue.operatorError}
                            classNameOperatorWidth="w-[100px]"
                            maxOptions={50}
                            field={
                              <FormField
                                label={t(
                                  'bookkeep.integrations.settings.payableRules.selectOption',
                                )}
                                visuallyHideLabel
                                className="customWidthForDropdown w-full"
                                alertMessage={subfilterValue.valueError}
                              >
                                <SupplierFilterField
                                  name={subfilterValueName}
                                  value={selectedKeys}
                                  onChange={(newValue) =>
                                    updateSubfilterValue({
                                      ...subfilterValue,
                                      value: newValue.length
                                        ? newValue
                                        : undefined,
                                    })
                                  }
                                  placement="bottom-end"
                                />
                              </FormField>
                            }
                          />
                        );
                      }

                      case 'costCenter': {
                        return (
                          <IdTypeField
                            value={subfilterValue}
                            onChange={updateSubfilterValue}
                            subfilterOperatorName={subfilterOperatorName}
                            subfilterValueName={subfilterValueName}
                            onSearch={onSearchCostCentersOptions}
                            onGetByKeys={onGetCostCentersOptionsByKeys}
                            maxOptions={50}
                            operatorError={subfilterValue.operatorError}
                            valueError={subfilterValue.valueError}
                            dropdownClassName="customWidthForDropdown"
                            classNameOperatorWidth="w-[100px]"
                          />
                        );
                      }

                      case 'grossAmount':
                      case 'vatAmount':
                      case 'netAmount':
                        return (
                          <AmountTypeField
                            value={subfilterValue}
                            onChange={updateSubfilterValue}
                            subfilterOperatorName={subfilterOperatorName}
                            subfilterValueName={subfilterValueName}
                            operatorError={subfilterValue.operatorError}
                            valueError={subfilterValue.valueError}
                            className="w-[100px] shrink-0"
                          />
                        );

                      default:
                        return <div className="w-full" />;
                    }
                  }}
                  conditionFieldName={conditionFieldName}
                  conditionOperatorName={conditionOperatorName}
                  conditionValueName={conditionValueName}
                />
              </div>
            </div>
            <VerticalLine height={40} className="vertical-line" />
            <div className="outputs-wrapper">
              <div className="mb-8 flex items-center gap-8">
                <HighlightIcon name="circle-play" size={40} variant="lemon" />
                <span className="title-l">
                  {t(
                    'bookkeep.integrations.settings.payableRules.outputsTitle',
                  )}
                </span>
              </div>
              <OutputsEditor
                fields={optionFields}
                outputs={outputs}
                conditions={conditions}
                onOutputsChange={setOutputs}
                dispatchOutputAction={
                  dispatchOutputAction as Dispatch<FilterBuilderOutputAction>
                }
                renderSubfilterValue={({
                  subfilterValue,
                  updateSubfilterValue,
                  subfilterValueName,
                }) => {
                  const [, customFieldId] =
                    subfilterValue.field?.match(/customField-(.*)/) ?? [];
                  // TODO: custom field switch bug, no options updated on switch
                  if (customFieldId) {
                    return (
                      <CustomFieldIdTypeOutput
                        customFieldId={customFieldId}
                        value={{ ...subfilterValue, operator: '' }}
                        onChange={updateSubfilterValue}
                        subfilterValueName={subfilterValueName}
                      />
                    );
                  }
                  switch (subfilterValue.field) {
                    case 'expenseAccount': {
                      return (
                        <FormField
                          label={t(
                            'bookkeep.integrations.settings.payableRules.selectOption',
                          )}
                          visuallyHideLabel
                          className="w-full"
                          alertMessage={subfilterValue.valueError}
                        >
                          <AutocompleteAsync
                            name={subfilterValueName}
                            value={
                              subfilterValue.value === ''
                                ? undefined
                                : {
                                    key: subfilterValue.value as string,
                                    label: '',
                                  }
                            }
                            loadValues={() => onLoadExpenseAccountsOptions()}
                            handleSearch={(value) =>
                              onSearchExpenseAccountsOptions(value || '')
                            }
                            handleSelect={(selected) =>
                              updateSubfilterValue({
                                ...subfilterValue,
                                value: selected?.key || null,
                              })
                            }
                            dropdownMenuContentMaxHeight="450px"
                            placeholder={t('misc.search')}
                            renderNoOptions={(rawValue) =>
                              autocompleteNoOptions(t, rawValue)
                            }
                            shouldFetchOnMount
                            isShouldSearchOnEmptyString
                          />
                        </FormField>
                      );
                    }

                    case 'vatAccount': {
                      return (
                        <FormField
                          label={t(
                            'bookkeep.integrations.settings.payableRules.selectOption',
                          )}
                          visuallyHideLabel
                          className="w-full"
                          alertMessage={subfilterValue.valueError}
                        >
                          <AutocompleteAsync
                            name={subfilterValueName}
                            value={
                              subfilterValue.value === ''
                                ? undefined
                                : {
                                    key: subfilterValue.value as string,
                                    label: '',
                                  }
                            }
                            handleSearch={async (search = '') => {
                              const accounts =
                                await onSearchTaxAccountsOptions(search);
                              return accounts.map(({ key, label }) => ({
                                key,
                                label,
                              }));
                            }}
                            loadValues={() => onSearchTaxAccountsOptions('')}
                            handleSelect={(selected) =>
                              updateSubfilterValue({
                                ...subfilterValue,
                                value: selected?.key || null,
                              })
                            }
                            dropdownMenuContentMaxHeight="450px"
                            renderNoOptions={(rawValue) =>
                              autocompleteNoOptions(t, rawValue)
                            }
                            placeholder={t('misc.search')}
                            shouldFetchOnMount
                            isShouldSearchOnEmptyString
                          />
                        </FormField>
                      );
                    }

                    case 'costCenter': {
                      return (
                        <FormField
                          label={t(
                            'bookkeep.integrations.settings.payableRules.selectOption',
                          )}
                          visuallyHideLabel
                          className="w-full"
                          alertMessage={subfilterValue.valueError}
                        >
                          <AutocompleteAsync
                            name={subfilterValueName}
                            value={
                              subfilterValue.value === ''
                                ? undefined
                                : {
                                    key: subfilterValue.value as string,
                                    label: '',
                                  }
                            }
                            handleSearch={async (search = '') => {
                              const searchCostCenters =
                                await onSearchCostCentersOptions(search);
                              return searchCostCenters.map(
                                ({ key, label }) => ({
                                  key,
                                  label,
                                }),
                              );
                            }}
                            loadValues={async () => {
                              const searchCostCenters =
                                await onSearchCostCentersOptions('');
                              return searchCostCenters.map(
                                ({ key, label }) => ({
                                  key,
                                  label,
                                }),
                              );
                            }}
                            dropdownMenuContentMaxHeight="450px"
                            handleSelect={(selected) => {
                              return updateSubfilterValue({
                                ...subfilterValue,
                                value: selected?.key || null, // Ensure the state is updated correctly
                              });
                            }}
                            renderNoOptions={(rawValue) =>
                              autocompleteNoOptions(t, rawValue)
                            }
                            placeholder={t('misc.search')}
                            shouldFetchOnMount
                            isShouldSearchOnEmptyString
                          />
                        </FormField>
                      );
                    }

                    default:
                      return <div className="w-full" />;
                  }
                }}
                outputFieldName={outputFieldName}
                outputValueName={outputValueName}
              />
            </div>
          </div>
        </PanelArea>
      </PanelBody>
      <PanelFooter>
        {payableRule ? (
          <div className="flex gap-8">
            <Button
              variant="secondaryNeutral"
              fit="parent"
              text={t('bookkeep.integrations.settings.payableRules.discard')}
              onClick={onBack}
            />
            <Button
              variant="primaryBrand"
              fit="parent"
              text={t('bookkeep.integrations.settings.payableRules.update')}
              onClick={() => {
                handleSave(isPayableRulePublished ? 'published' : 'draft');
              }}
            />
          </div>
        ) : (
          <div className="flex gap-8">
            <Button
              variant="secondaryNeutral"
              fit="parent"
              text={t(
                'bookkeep.integrations.settings.payableRules.saveAsDraft',
              )}
              onClick={() => handleSave('draft')}
            />
            <Button
              variant="primaryBrand"
              fit="parent"
              text={t('bookkeep.integrations.settings.payableRules.publish')}
              onClick={() => handleSave('published')}
            />
          </div>
        )}
      </PanelFooter>
    </>
  );
};

/**
 * Convert all possible options from the filters v2 into ones that's supported by payable rules
 */
const extractFields = (
  fields: Field[],
  targetFieldOptions: string[],
): Field[] => {
  const extractedFields: Field[] = [];

  fields.forEach((field) => {
    if (field.options) {
      const matchingOptions = field.options.filter(
        (option) =>
          targetFieldOptions.includes(option.key) ||
          option.key.includes('customField-'),
      );

      if (matchingOptions.length > 0) {
        extractedFields.push({
          key: field.key,
          label: field.label,
          options: matchingOptions,
          kind: field.kind,
        });
      }
    }
  });

  return extractedFields;
};

export const CustomFieldIdTypeOutput = ({
  customFieldId,
  value,
  onChange,
  subfilterValueName,
}: {
  customFieldId: string;
  value: SubfilterValueWithId & {
    valueError?: string;
  };
  onChange: (value: SubfilterValueWithId) => void;
  subfilterValueName: string;
}) => {
  const { t } = useTranslation('global');

  const onSearchCustomFieldValues = useSearchCustomFieldValues(customFieldId);
  const onSearchCustomFieldValuesOptions = async (search: string) => {
    const customFieldValues = await onSearchCustomFieldValues(
      Array.isArray(search) ? search[0] : search,
    );
    return customFieldValues.map(({ id, name }) => ({ key: id, label: name }));
  };

  const selectedKey = Array.isArray(value.value) ? value.value[0] : value.value;
  const customFields = unwrapQuery(
    useCustomFieldsQuery({ includeArchived: false }),
  );
  const customField = [
    customFields?.expenseCategoryField,
    ...(customFields?.customFields ?? []),
  ].find((field) => field?.id === customFieldId);
  const getBooleanValues = (cf: CustomFieldDefinition) => {
    const trueValue = cf.custom_fields_values.find((cfValue) =>
      ['true', '1'].includes(cfValue.value),
    );
    const falseValue = cf.custom_fields_values.find((cfValue) =>
      ['false', '0'].includes(cfValue.value),
    );

    if (!trueValue || !falseValue) {
      return;
    }

    return [
      {
        value: trueValue.id,
        label: t('misc.yes'),
      },
      {
        value: falseValue.id,
        label: t('misc.no'),
      },
    ];
  };
  const booleanValues = customField && getBooleanValues(customField);
  let rawValue = value.value as string | null | undefined;
  if (booleanValues && !rawValue) {
    rawValue = booleanValues[0].value;
    // trigger on change event so we consider output complete
    onChange({
      ...value,
      value: rawValue,
    });
  }
  return (
    <>
      {booleanValues && (
        <OptionGroup
          className="w-full"
          name="customFieldBoolean"
          options={booleanValues}
          value={rawValue || null}
          onChange={(event) => {
            const newValue = event.target.value;
            onChange({
              ...value,
              value: newValue ?? undefined,
            });
          }}
        />
      )}
      {!booleanValues && (
        <FormField
          label={t('bookkeep.integrations.settings.payableRules.selectOption')}
          visuallyHideLabel
          className="w-full"
          alertMessage={value.valueError}
        >
          <AutocompleteAsync
            name={subfilterValueName}
            value={
              selectedKey === '' ? undefined : { key: selectedKey, label: '' }
            }
            loadValues={() => onSearchCustomFieldValuesOptions('')}
            handleSearch={(search) =>
              onSearchCustomFieldValuesOptions(search || '')
            }
            handleSelect={(selected) =>
              onChange({
                ...value,
                value: selected?.key || '',
              })
            }
            dropdownMenuContentMaxHeight="450px"
            renderNoOptions={(rawTypeValue) =>
              autocompleteNoOptions(t, rawTypeValue)
            }
            placeholder={t(
              'bookkeep.integrations.settings.payableRules.outputs.customFieldSelectValue',
            )}
            shouldFetchOnMount
            isShouldSearchOnEmptyString
          />
        </FormField>
      )}
    </>
  );
};

const translateCondition = (condition: SubfilterValue) => {
  const transformedCondition = { ...condition };

  switch (condition.operator) {
    case 'isEmpty':
      transformedCondition.operator = '=';
      transformedCondition.value = null;
      break;
    case 'isNotEmpty':
      transformedCondition.operator = '!=';
      transformedCondition.value = null;
      break;
    case 'is':
      transformedCondition.operator = '=';
      break;
    case 'isNot':
      transformedCondition.operator = '!=';
      break;
    case '= (is)':
      transformedCondition.operator = '=';
      break;
    case '!= (is not)':
      transformedCondition.operator = '!=';
      break;
    case '< (less)':
      transformedCondition.operator = '<';
      break;
    case '> (greater)':
      transformedCondition.operator = '>';
      break;
    case '>= (greater or equal)':
      transformedCondition.operator = '>=';
      break;
    case '<= (less or equal)':
      transformedCondition.operator = '<=';
      break;
    case 'isBetween':
      transformedCondition.operator = 'between';
      break;
    case 'isNotBetween':
      transformedCondition.operator = 'notBetween';
      break;
    default:
      // No transformation needed for other operators
      break;
  }
  return transformedCondition;
};

interface Payload {
  icon: string;
  conditions: SubfilterValue[];
  outputs: Output[];
  state: 'draft' | 'published';
  name: string;
}

const transformPayload = (payload: Payload) => {
  return {
    ...payload,
    conditions: payload.conditions
      // TODO: figure out a way to allow empty or null only for isEmpty
      .map((condition) => {
        if (condition.field.startsWith('customField-')) {
          const customFieldKey = condition.field.replace('customField-', '');
          const transformedCondition = translateCondition({
            ...condition,
            field: 'customField',
          });
          return {
            ...transformedCondition,
            value: {
              key: customFieldKey,
              value: (() => {
                if (condition.value === null) {
                  return null;
                }
                return Array.isArray(condition.value)
                  ? condition.value
                  : [condition.value];
              })(),
            },
          };
        }
        return translateCondition(condition);
      }),
    outputs: payload.outputs.reduce(
      (
        accumulator: { [key: string]: string | { [key: string]: string } },
        output,
      ) => {
        if (output.field.startsWith('customField-')) {
          const customFieldKey = output.field.replace('customField-', '');
          if (!accumulator.customField) {
            accumulator.customField = {};
          }
          (accumulator.customField as { [key: string]: string })[
            customFieldKey
          ] = output.value;
        } else {
          accumulator[output.field] = output.value;
        }
        return accumulator;
      },
      {},
    ),
  };
};

const getOperator = (isEquals: boolean, hasNoValue: boolean): string => {
  if (hasNoValue) {
    return isEquals ? 'isEmpty' : 'isNotEmpty';
  }
  return isEquals ? 'is' : 'isNot';
};

const handleCustomField = (
  condition: EnrichedPayableRule['rawConditions'][number],
) => {
  // Safe to cast since we've checked field type
  const customValue = condition.value as {
    key: string;
    value: string[] | null;
  };
  const isEquals = condition.operator === '=';
  const hasNoValue = customValue.value === null;

  return {
    ...condition,
    field: `customField-${customValue.key}`,
    operator: getOperator(isEquals, hasNoValue),
    value: hasNoValue ? null : customValue.value,
  };
};

const handleIdType = (
  condition: EnrichedPayableRule['rawConditions'][number],
) => {
  const isEquals = condition.operator === '=';
  const hasNoValue = condition.value === null;

  return {
    ...condition,
    operator: getOperator(isEquals, hasNoValue),
    value: hasNoValue ? null : condition.value,
  };
};

const handleAmount = (
  condition: EnrichedPayableRule['rawConditions'][number],
) => {
  const operatorMap = {
    between: 'isBetween',
    notBetween: 'isNotBetween',
  } as const;

  return {
    ...condition,
    operator:
      operatorMap[condition.operator as keyof typeof operatorMap] ||
      condition.operator,
  };
};

const convertConditionsFromPayableRuleToForm = (
  conditions: EnrichedPayableRule['rawConditions'],
): Condition[] => {
  return conditions.map((condition) => {
    if (condition.field === 'customField') {
      return handleCustomField(condition);
    }

    if (
      ['payableType', 'supplier', 'requestor', 'costCenter'].includes(
        condition.field,
      )
    ) {
      return handleIdType(condition);
    }

    if (condition.field === 'grossAmount') {
      return handleAmount(condition);
    }

    return condition;
  });
};

const convertOutputsToArray = (
  outputs: EnrichedPayableRule['rawOutputs'],
): Output[] => {
  return Object.entries(outputs).flatMap(([key, value]) => {
    let computedValue = value;
    if (typeof value === 'object' && value !== null && 'id' in value) {
      computedValue = value.id;
    }
    if (key === 'customField') {
      return Object.entries(value).map(([field, fieldValue]) => ({
        field: `customField-${field}`,
        value: fieldValue,
      }));
    }

    return {
      field: key,
      value: computedValue,
    } as Output;
  });
};
